import {Component, EventEmitter, HostListener, Input, OnInit, Output, OnDestroy, Inject, PLATFORM_ID} from '@angular/core';
import {CircleComponent} from '../../../../elements/circle/circle.component';
import {TextsComponent} from '../../../../elements/texts/texts.component';
import {Location, NgIf, isPlatformBrowser} from '@angular/common';
import {animate, state, style, transition, trigger} from '@angular/animations';
import {Continent, ContinentIcons, ContinentUrls} from '../../../../../model/circle';
import {CircleDestinations} from '../../../../../model/circle-destinations';
import {LocationDto} from '../../../../../model/continent';
import {ContinentService} from '../../../../../services/continent-service';
import {PaginationParams} from '../../../../../model/pagination';
import {environment} from '../../../../../environments/environment';
import {CountryService} from '../../../../../services/country-service';
import {Router} from '@angular/router';
import {scrollUp} from '../../../../../functions/scroll-up';
import {Text} from '../../../../../model/text';
import {debounceTime, fromEvent, Subscription} from 'rxjs';

export type MainDestinationsMode = 'Continents' | 'Countries';

@Component({
  selector: 'app-main-destinations',
  imports: [
    CircleComponent,
    TextsComponent,
    NgIf,
  ],
  templateUrl: './main-destinations.component.html',
  standalone: true,
  styleUrl: './main-destinations.component.scss',
  animations: [
    trigger('prev', [
      state('void', style({
        clipPath: 'circle(0% at 4% 12%)',
      })),
      transition(':enter', [
        animate('500ms ease-out', style({
          clipPath: 'circle(100% at 50% 50%)'
        })),
      ]),
    ]),
    trigger('next', [
      state('void', style({
        clipPath: 'circle(0% at 4% 88%)',
      })),
      transition(':enter', [
        animate('500ms ease-out', style({
          clipPath: 'circle(100% at 50% 50%)'
        })),
      ]),
    ])
  ]
})
export class MainDestinationsComponent implements OnInit, OnDestroy {
  constructor(
    private continentService: ContinentService,
    private countryService: CountryService,
    private router: Router,
    public location: Location,
    @Inject(PLATFORM_ID) private platformId: any
  ) {
    this.isBrowser = isPlatformBrowser(this.platformId);
  }

  @Input() fullRadius: boolean = false;
  @Input() mode: MainDestinationsMode = 'Countries';
  @Input() locationsDto: LocationDto[] = [];

  @Output() clickOn = new EventEmitter();
  @Output() ClickOnActive = new EventEmitter();
  @Output() emitContinent = new EventEmitter();
  @Output() loadingEmit = new EventEmitter();

  loadingCircle: boolean = false;
  title: Text[] = [
    {text: [' ', 'Top '], fontFamily: 'pd', fontStyle: 'italic'},
    {text: ['Континенты ', 'destinations ']},
  ];

  destinationsArray: CircleDestinations = new CircleDestinations({
    destinations: {
      titles: [],
      icons: [],
      urls: [],
    },
    backgrounds: []
  });

  defaultParams = new PaginationParams({
    sort: 'titleEn:ASC',
    query: '',
    pageSize: 1000,
    pageNumber: 0,
  });

  params: PaginationParams = new PaginationParams(this.defaultParams);

  private isBrowser: boolean;
  private resizeSubscription?: Subscription;
  private lastWindowHeight: number = 0;
  private resizeTimeout: any;
  private isIOS = false;
  private vhFixed = false;

  stableWindowHeight: number = 0;
  stableWindowWidth: number = 0;

  @HostListener('window:resize', ['$event'])
  onResize(event: Event) {
    if (!this.isBrowser) return;
    this.handleResize();
  }

  isMobile: boolean = false;

  activeBack: number = 0;
  prevBack: boolean = false;
  nextBack: boolean = false;
  circleStyle: string = '';

  ngOnInit(): void {
    if (this.isBrowser) {
      this.detectIOS();
      this.initializeStableDimensions();
      this.setVhVariable();
      this.resizeSubscription = fromEvent(window, 'resize')
        .pipe(debounceTime(100))
        .subscribe(() => {
          this.handleResize();
        });
    }
    if (window.innerWidth < 570) {
      this.isMobile = true;
    }
    if (this.location.path() === '') {
      this.loadingEmit.emit(true);
    }
    this.getData();
    this.setCircleSize();
    if (this.mode === 'Countries') {
      this.title = [
        {text: [' ', 'Top '], fontFamily: 'pd', fontStyle: 'italic'},
        {text: ['Страны ', 'destinations ']},
      ];
    }
    if (this.mode === 'Continents' && this.isMobile && this.location.path() === '/destinations') {
      this.title = [{text: ['Континенты ', 'destinations ']},]
    }
    if (this.isBrowser && window.innerWidth < 571) {
      this.fullRadius = false;
    }
  }

  ngOnDestroy(): void {
    if (this.resizeSubscription) {
      this.resizeSubscription.unsubscribe();
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  private detectIOS(): void {
    this.isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
  }

  private initializeStableDimensions(): void {
    if (!this.isBrowser) return;
    this.stableWindowHeight = document.documentElement.clientHeight || window.innerHeight;
    this.stableWindowWidth = document.documentElement.clientWidth || window.innerWidth;
    this.lastWindowHeight = this.stableWindowHeight;
  }

  private handleResize(): void {
    if (!this.isBrowser) return;
    const currentHeight = window.innerHeight;
    const heightChange = Math.abs(currentHeight - this.lastWindowHeight);
    if (this.isIOS && heightChange < 100 && heightChange > 10) {
      return;
    }
    this.stableWindowHeight = document.documentElement.clientHeight || currentHeight;
    this.stableWindowWidth = document.documentElement.clientWidth || window.innerWidth;
    this.lastWindowHeight = currentHeight;
    clearTimeout(this.resizeTimeout);
    this.resizeTimeout = setTimeout(() => {
      this.setCircleSize();
      this.setVhVariable();
    }, 150);
  }

  private setVhVariable(): void {
    if (!this.isBrowser) return;
    const vh = this.stableWindowHeight * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    if (this.isIOS && !this.vhFixed) {
      setTimeout(() => {
        document.documentElement.style.setProperty('--vh-fixed', `${vh}px`);
        this.vhFixed = true;
      }, 500);
    }
  }

  initDestinationsArray() {
    if (this.mode === 'Continents') {
      this.destinationsArray = new CircleDestinations({
        destinations: {
          titles: this.locationsDto.map(loc => [loc.titleRu, loc.titleEn]),
          icons: ContinentIcons,
          urls: ContinentUrls,
        },
        backgrounds: this.locationsDto.map(loc => loc.image),
        cssStyleImage: this.locationsDto.map(loc => loc.cssStyleImage ?? ''),
      });
    } else {
      this.destinationsArray = new CircleDestinations({
        destinations: {
          titles: this.locationsDto.map(loc => [loc.titleRu, loc.titleEn]),
          icons: this.locationsDto.map(loc => ({src: loc.icon ?? '', alt: loc.titleEn})),
          urls: this.locationsDto.map(loc => loc.url),
        },
        backgrounds: this.locationsDto.map(loc => loc.image),
        cssStyleImage: this.locationsDto.map(loc => loc.cssStyleImage ?? ''),
      });
    }
  }

  getData() {
    this.loadingCircle = true;
    if (this.mode === 'Continents') {
      this.continentService.getContinentList(this.params).subscribe((data) => {
        const content = data.content ?? [];
        const antarcticaIndex = content.findIndex(item => item.url === 'antarctica');
        if (antarcticaIndex > -1) {
          this.locationsDto = [
            content[antarcticaIndex],
            ...content.slice(0, antarcticaIndex),
            ...content.slice(antarcticaIndex + 1)
          ];
        } else {
          this.locationsDto = content;
        }
        // this.emitContinent.emit(this.locationsDto[0]);
        this.initDestinationsArray();
        this.loadingCircle = false;
        if (this.location.path() === '') {
          this.loadingEmit.emit(false);
        }
      });
    } else {
      this.countryService.getCountryList(this.params).subscribe((data) => {
        this.locationsDto = data.content?.filter((v) => v.displayPage === true) ?? [];
        this.initDestinationsArray();
        this.loadingCircle = false;
      });
    }
  }

  setback(active: number) {
    if (!this.nextBack && !this.prevBack) {
      if (active == this.activeBack) {
        this.clickOn.emit(active);
      } else {
        const prev = (active == this.activeBack - 1) || ((this.activeBack == 0) && (active == this.destinationsArray.backgrounds.length - 1));
        if (prev) {
          this.setPrev(active);
        } else {
          this.setNext(active);
        }
      }
    }
  }

  setNext(active: number) {
    this.nextBack = true;
    setTimeout(() => {
      this.activeBack = active;
      this.nextBack = false;
    }, 500);
  }

  setPrev(active: number) {
    this.prevBack = true;
    setTimeout(() => {
      this.activeBack = active;
      this.prevBack = false;
    }, 500);
  }

  emitObj(obj: Continent) {
    if (!obj?.title || obj.title.length < 2) {
      return;
    }
    const searchTitle = obj.title[1];
    const foundLocation = this.locationsDto.find(location =>
      location.titleEn === searchTitle
    );
    if (foundLocation?.url) {
      if (this.mode === 'Continents') {
        scrollUp();
        this.router.navigate(['/destinations/' + foundLocation.url]).then(r => r);
      } else {
        scrollUp();
        this.router.navigate(['/destinations/' + foundLocation.continent?.url + '/' + foundLocation.url]).then(r => r);
      }
    }
  }

  getBackground(){
    return `url("${environment.s3Prefix + this.destinationsArray.backgrounds[this.activeBack]}")`;
  }

  getStyle() {
    if (
      !this.destinationsArray?.cssStyleImage ||
      this.activeBack === undefined ||
      !this.destinationsArray.cssStyleImage[this.activeBack]
    ) {
      return '';
    }
    return this.destinationsArray.cssStyleImage[this.activeBack];
  }

  getBackgroundPrev() {
    const index = this.activeBack - 1 > -1 ? this.activeBack - 1 : this.destinationsArray.backgrounds.length - 1;
    return `url("${environment.s3Prefix + this.destinationsArray.backgrounds[index]}")`;
  }

  getStylePrev() {
    const index = this.activeBack - 1 > -1 ? this.activeBack - 1 : this.destinationsArray.backgrounds.length - 1;
    if (
      !this.destinationsArray?.cssStyleImage ||
      index === undefined ||
      !this.destinationsArray.cssStyleImage[index]
    ) {
      return '';
    }
    return this.destinationsArray.cssStyleImage[index];
  }

  getBackgroundNext() {
    const index = this.activeBack + 1 > this.destinationsArray.backgrounds.length - 1 ? 0 : this.activeBack + 1;
    return `url("${environment.s3Prefix + this.destinationsArray.backgrounds[index]}")`;
  }

  getStyleNext() {
    const index = this.activeBack + 1 > this.destinationsArray.backgrounds.length - 1 ? 0 : this.activeBack + 1;
    if (
      !this.destinationsArray?.cssStyleImage ||
      index === undefined ||
      !this.destinationsArray.cssStyleImage[index]
    ) {
      return '';
    }
    return this.destinationsArray.cssStyleImage[index];
  }

  setCircleSize() {
    if (!this.isBrowser) return;
    const height = this.stableWindowHeight;
    const scale = height * 0.62 / 600;
    const mr = 372 * scale;
    this.circleStyle = `transform: scale(${scale}); margin-left: -${mr}px`;
  }

  getRadius() {
    return this.fullRadius ? 'destinations-container-full' : 'destinations-container-top';
  }

  setFilter(obj: Continent) {
    if (!obj?.title || obj.title.length < 2) {
      return;
    }
    const searchTitle = obj.title[1];
    const foundLocation = this.locationsDto.find(location =>
      location.titleEn === searchTitle
    );
    this.emitContinent.emit(foundLocation);
  }

  get marginTitle() {
    if (!this.isBrowser) return '';
    const width = this.stableWindowWidth;
    const height = this.stableWindowHeight;

    if (width < 571) {
      if (this.location.path() === '/destinations') {
        return 'margin-top: 15px'
      }
      return 'margin-top: 61px'
    }
    if (this.fullRadius) {
      if (width >= 1080 && width > 1440) {
        return 'margin-top: 60px;'
      }
      if (width <= 1440 && width >= 1080) {
        if (height <= 600) {
          return 'margin-top: 30px;'
        }
        if (height <= 800 && height > 600) {
          return 'margin-top: 40px;'
        }
        return 'margin-top: 60px;'
      }
      return height <= 600 ? 'margin-top: 30px;' : 'margin-top: 40px;'
    } else {
      if (width >= 1080 && width > 1440) {
        return 'margin-top: 100px;'
      }
      if (width <= 1440 && width >= 1080) {
        if (height <= 600) {
          return 'margin-top: 80px;'
        }
        if (height <= 800 && height > 600) {
          return 'margin-top: 100px;'
        }
        return 'margin-top: 100px;'
      }
      return height <= 600 ? 'margin-top: 80px;' : 'margin-top: 100px;'
    }
  }

  get circlePosition() {
    if (!this.isBrowser) return '';
    const width = this.stableWindowWidth;
    const height = this.stableWindowHeight;
    if (width < 571) {
      // return 'top:60px; left: -5px'
      return 'left: -9%'
    }
    if (this.fullRadius) {
      if (width >= 1080 && width > 1440) {
        return 'top: 90px; left: 60px'
      }
      if (width <= 1440 && width >= 1080) {
        if (height <= 600) {
          return 'left: -80px; top: 45px'
        }
        if (height <= 800 && height > 600) {
          return 'left: -40px; top: 80px'
        }
        return 'top: 90px; left: 60px'
      }
      return height <= 600 ? 'left: -80px; top: 45px' : 'left: -40px; top: 80px'
    } else {
      if (width >= 1080 && width > 1440) {
        return 'top: 60px; left: 80px'
      }
      if (width <= 1440 && width >= 1080) {
        if (height <= 600) {
          return 'left: -55px; top: 90px'
        }
        if (height <= 800 && height > 600) {
          return 'left: -10px; top: 90px'
        }
        return 'top: 60px; left: 80px'
      }
      return height <= 600 ? 'left: -55px; top: 90px' : 'left: -10px; top: 90px'
    }
  }

  protected readonly window = window;
}
